// src/pages/ResetPassword.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const emailTrimmed = useMemo(() => email.trim(), [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(emailTrimmed, {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) throw error;

      setSuccess(true);
    } catch (err) {
      console.error("Erreur réinitialisation:", err);
      setError(err?.message || t("resetPasswordPage.errors.send"));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-serif font-bold mb-3">
              {t("resetPasswordPage.success.title")}
            </h2>

            <p className="text-neutral-600 mb-6 leading-relaxed">
              {t("resetPasswordPage.success.messageBefore")}{" "}
              <strong>{emailTrimmed}</strong>.
              <br />
              {t("resetPasswordPage.success.messageAfter")}
            </p>

            <div className="flex flex-col gap-3">
              <Link to="/login" className="btn-artsy btn-primary-artsy w-full">
                {t("resetPasswordPage.actions.backToLogin")}
              </Link>

              <Link
                to="/explore"
                className="w-full py-3 border border-black text-black rounded-xl hover:bg-black hover:text-white transition-all font-medium"
              >
                {t("resetPasswordPage.actions.explorePlatform")}
              </Link>
            </div>

            <p className="mt-5 text-xs text-neutral-500">
              {t("resetPasswordPage.success.portalNote")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Colonne gauche: image */}
      <div className="lg:w-1/2 relative bg-gray-900 hidden lg:block">
        <img
          src={`${process.env.PUBLIC_URL}/images/ui/login_bg.png`}
          alt={t("resetPasswordPage.imageAlt")}
          className="absolute inset-0 w-full h-full object-cover opacity-75"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-12 bg-black bg-opacity-45">
          <h1 className="display-title text-5xl mb-6 font-light tracking-tight">
            Artkiv
          </h1>
          <p className="max-w-md text-center text-lg leading-relaxed text-white/90">
            {t("resetPasswordPage.hero.line1")}
            <br />
            {t("resetPasswordPage.hero.line2")}
          </p>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/login"
              className="px-6 py-3 border border-white text-white hover:bg-white hover:text-black font-medium transition-colors"
            >
              {t("resetPasswordPage.actions.backToLogin")}
            </Link>
            <Link
              to="/explore"
              className="px-6 py-3 bg-white text-black hover:bg-gray-100 font-medium transition-colors"
            >
              {t("resetPasswordPage.actions.explore")}
            </Link>
          </div>
        </div>
      </div>

      {/* Colonne droite: formulaire */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-10">
            <h1 className="display-title text-3xl mb-3 font-light">Artkiv</h1>
            <p className="text-neutral-600">
              {t("resetPasswordPage.mobileIntro")}
            </p>
          </div>

          <h2 className="font-serif text-2xl mb-2">{t("resetPasswordPage.form.title")}</h2>
          <p className="text-neutral-600 mb-8 leading-relaxed">
            {t("resetPasswordPage.form.descriptionLine1")}
            <br />
            {t("resetPasswordPage.form.descriptionLine2")}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm mb-2">{t("resetPasswordPage.form.emailLabel")}</label>
              <input
                type="email"
                placeholder={t("resetPasswordPage.form.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control-artsy w-full"
                required
                disabled={loading}
              />
              <p className="mt-2 text-xs text-neutral-500">
                {t("resetPasswordPage.form.emailHint")}
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              className="btn-artsy btn-primary-artsy w-full"
              disabled={loading}
            >
              {loading ? t("resetPasswordPage.form.sending") : t("resetPasswordPage.form.submit")}
            </button>
          </form>

          <div className="mt-8 text-center space-y-3">
            <Link to="/login" className="text-sm text-gray-700 hover:text-black">
              ← {t("resetPasswordPage.actions.backToLogin")}
            </Link>

            <div>
              <Link
                to="/explore"
                className="text-sm text-gray-600 hover:text-black underline underline-offset-4"
              >
                {t("resetPasswordPage.actions.exploreFirst")}
              </Link>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-neutral-200 text-center">
            <p className="text-xs text-neutral-500">
              {t("resetPasswordPage.footerHelp")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
